import React, { useState, useCallback, useEffect } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Link from '@atlaskit/link';
import { Stack, Text } from '@atlaskit/primitives/compiled';

import { isFedRamp } from '@atlassian/atl-context';
import AIOptIn from '@atlassian/ai-opt-in';

import { AIUpsell } from '@confluence/change-edition/entry-points/AIUpsell';
import { useIsSiteAdmin } from '@confluence/current-user/entry-points/useIsSiteAdmin';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

import EditorAIOptInBanner from './assets/editor-ai-opt-in-banner.svg';

type OptInStatus = 'enabled' | 'disabled' | 'disabled-opt-in';
export type AIGlobalOptIn = {
	optInStatus: OptInStatus;
	triggerOptInFlow: () => void;
	AIOptInModal: React.FC;
};

interface EditorAIOptInParams {
	isAdminHubAIEnabled: boolean;
	edition: ConfluenceEdition | null;
	isAIEnabledForContent?: boolean;
	orgId?: string;
	environment?: string;
}

const WAC_LINK = 'https://www.atlassian.com/trust/atlassian-intelligence';

const messages = defineMessages({
	title: {
		id: 'editor-features.ai-optin-modal.title',
		defaultMessage: 'Elevate your team’s writing',
		description: 'Title for Editor AI Opt In Modal',
	},
	description: {
		id: 'editor-features.ai-optin-modal.description',
		defaultMessage:
			'Help enhance your team’s writing with Rovo. Let AI improve clarity, maintain a professional tone, or craft concise content.',
		description: 'Description for Editor AI Opt In Modal',
	},
	readMoreText: {
		id: 'editor-features.ai-optin-modal.learn-more.link.text',
		defaultMessage: '<a>Read about Rovo</a>',
		description: 'Read more link text for AI Opt In Dialog',
	},
	adminTurnOnText: {
		id: 'editor-features.ai-optin-modal.admin-turn-on-text',
		defaultMessage: 'Turn on Rovo features for your users from Atlassian Administration.',
		description: 'Turn on AI description for Editor AI Opt In Modal',
	},
	closeCTAText: {
		id: 'editor-features.ai-optin-modal.close-cta-text',
		defaultMessage: 'Maybe later',
		description: 'Close CTA text for Editor AI Opt In Modal',
	},
	getStartCTAText: {
		id: 'editor-features.ai-optin-modal.get-started-cta-text',
		defaultMessage: 'Get started',
		description: 'Get started CTA text for Editor AI Opt In Modal',
	},
	learnMoreText: {
		id: 'editor-features.ai-optin-modal.learn-more-cta-text',
		defaultMessage: 'Learn more',
		description: 'Learn more text for Editor AI Opt In Modal',
	},
	upgradePlanText: {
		id: 'editor-features.ai-optin-modal.upgrade-plan-text',
		defaultMessage: 'To start using Rovo in your products, upgrade your plan.',
		description: 'Upgrade plan to use Rovo text',
	},
	upsellText: {
		id: 'editor-features.ai-optin-modal.upsell-text',
		defaultMessage:
			'With Rovo (AI), your team can craft concise, clear, and professional content faster than ever.',
		description: 'Upsell text for Editor AI Upsell modal',
	},
	freePlanText: {
		id: 'editor-features.ai-optin-modal.free-plan-text',
		defaultMessage: 'You’re on the free plan.',
		description: 'User is on free plan',
	},
	standardPlanText: {
		id: 'editor-features.ai-optin-modal.standard-plan-text',
		defaultMessage: 'You’re on the standard plan.',
		description: 'User is on standard plan',
	},
});

const OptInModal = ({
	onClose,
	edition,
	isSiteAdmin,
	orgId,
	environment,
}: {
	onClose: () => void;
	edition: ConfluenceEdition;
	isSiteAdmin: boolean;
	orgId?: string;
	environment?: string;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const adminLink = getOptInDeepLink(orgId, environment);
	const { Modal } = useChoreographerAssets();

	const onOptInReadMoreClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.stopPropagation();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'editorAI',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'editorAIOptInReadMoreLink',
					attributes: {
						product: 'confluence',
						isOrgAdmin: isSiteAdmin,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isSiteAdmin],
	);

	const showUpsell = edition !== ConfluenceEdition.PREMIUM;

	/**
	 * HOT-116592 - https://atlassian.enterprise.slack.com/archives/C08MHDT1KLJ
	 * This is used to track if the AIUpsell and / or AIOptIn components are rendered
	 */
	const onRender = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'editorAI',
				action: 'triggerOptInFlowModalRendered',
				actionSubject: 'editorAIOptInDialog',
				actionSubjectId: 'editorAIModalRendered',
				attributes: {
					product: 'confluence',
					isOrgAdmin: isSiteAdmin,
					edition,
					env: environment,
					upsell: showUpsell,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin, showUpsell, edition, environment]);

	return (
		<Modal
			width="fit-content"
			onClose={onClose}
			testId={showUpsell ? 'editor-ai-upsell-modal' : 'editor-ai-opt-in-modal'}
			/**
			 * HOT-116592 - https://atlassian.enterprise.slack.com/archives/C08MHDT1KLJ
			 * Mark this modal as transactional as its a critical part of the ai upsell / opt in
			 */
			messageType="transactional"
			messageId="editor-ai-opt-in-modal"
		>
			{showUpsell ? (
				<AIUpsell
					feature="editorAI"
					title={formatMessage(messages.title)}
					body={`${formatMessage(messages.upsellText)}
          <p></p>
          <p>${
						edition === ConfluenceEdition.FREE
							? formatMessage(messages.freePlanText)
							: formatMessage(messages.standardPlanText)
					} ${formatMessage(messages.upgradePlanText)}</p>`}
					image={{
						src: { default: EditorAIOptInBanner, dark: EditorAIOptInBanner },
						alt: 'editor-ai-opt-in-banner',
					}}
					learnMoreLink={{
						message: formatMessage(messages.learnMoreText),
						href: WAC_LINK,
					}}
					onRender={onRender}
				/>
			) : (
				<AIOptIn
					source="editorAI"
					product="confluence"
					isOrgAdmin
					onAdminClose={onClose}
					// TODO: Update the attribute below so it is translated.
					// eslint-disable-next-line no-restricted-syntax
					image={<img src={EditorAIOptInBanner} height="96px" alt="editor-ai-opt-in-banner" />}
					adminLink={adminLink}
					adminHeaderText={formatMessage(messages.title)}
					adminCloseCTAText={formatMessage(messages.closeCTAText)}
					adminCTAText={formatMessage(messages.getStartCTAText)}
					adminBodyText={
						<Stack space="space.150">
							<Text>
								{formatMessage(messages.description)}{' '}
								<FormattedMessage
									{...messages.readMoreText}
									values={{
										a: (s: React.ReactNode[]) => (
											<Link
												href={WAC_LINK}
												target="_blank"
												onClick={onOptInReadMoreClick}
												rel="noopener noreferrer"
											>
												{s}
											</Link>
										),
									}}
								/>
							</Text>
							<Text>{formatMessage(messages.adminTurnOnText)}</Text>
						</Stack>
					}
					onRender={onRender}
				/>
			)}
		</Modal>
	);
};

const getOptInDeepLink = (orgId?: string, environment?: string) => {
	if (!orgId || !environment) {
		return undefined;
	}

	if (orgId) {
		if (environment === 'PRODUCTION') {
			return `https://admin.atlassian.com/o/${orgId}/atlassian-intelligence`;
		} else if (environment === 'STAGING') {
			return `https://admin.stg.atlassian.com/o/${orgId}/atlassian-intelligence`;
		}
	}
};

const getOptInStatus = (
	isAdminHubAIEnabled: boolean,
	isAIEnabledForContent: boolean,
	isSiteAdmin: boolean,
	edition: ConfluenceEdition | null,
): OptInStatus => {
	if (isFedRamp()) {
		return 'disabled';
	}

	if (!isAIEnabledForContent && isAdminHubAIEnabled) {
		return 'disabled';
	}

	if (isAdminHubAIEnabled) {
		return 'enabled';
	}

	if (edition === null) {
		return 'disabled';
	}

	if (isSiteAdmin) {
		return 'disabled-opt-in';
	}

	return 'disabled';
};

const defaultEmptyTrigger = () => {};
const defaultEmptyComponent = () => null;

export const useEditorAIOptIn = ({
	isAdminHubAIEnabled,
	isAIEnabledForContent = true,
	edition,
	orgId,
	environment,
}: EditorAIOptInParams): AIGlobalOptIn => {
	const isSiteAdmin = useIsSiteAdmin().isSiteAdmin;

	const optInStatus = getOptInStatus(
		isAdminHubAIEnabled,
		isAIEnabledForContent,
		isSiteAdmin,
		edition,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (optInStatus === 'disabled-opt-in') {
			const preloadImages = (paths: string[]) => {
				paths.forEach((path) => {
					const img = new Image();
					img.src = path;
				});
			};
			preloadImages([EditorAIOptInBanner]);
		}
	}, [optInStatus]);

	const [showOptInModal, setShowOptInModal] = useState(false);

	const triggerOptInFlow = useCallback(() => {
		if (edition !== null) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'editorAI',
					action: 'triggerOptInFlow',
					actionSubject: 'editorAIOptInDialog',
					actionSubjectId: 'editorAIOptInTrigger',
					attributes: {
						isOrgAdmin: isSiteAdmin,
						optInType: edition === ConfluenceEdition.PREMIUM ? 'AIOptIn' : 'AIUpsell',
					},
				},
			}).fire();
		}
		setShowOptInModal(true);
	}, [setShowOptInModal, createAnalyticsEvent, edition, isSiteAdmin]);

	const AIOptInModal = useCallback(() => {
		const onCloseModal = () => setShowOptInModal(false);

		return showOptInModal && edition !== null ? (
			<OptInModal
				onClose={onCloseModal}
				edition={edition}
				isSiteAdmin={isSiteAdmin}
				orgId={orgId}
				environment={environment}
			/>
		) : null;
	}, [showOptInModal, edition, isSiteAdmin, orgId, environment]);

	if (optInStatus === 'disabled-opt-in') {
		return {
			optInStatus,
			triggerOptInFlow,
			AIOptInModal,
		};
	}

	return {
		optInStatus,
		triggerOptInFlow: defaultEmptyTrigger,
		AIOptInModal: defaultEmptyComponent,
	};
};

export const getSynchronyEditorAIOptInStatus = (isAdminHubAIEnabled: boolean): OptInStatus => {
	return isAdminHubAIEnabled ? 'enabled' : 'disabled';
};
